import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { EEParticipationStatusStatisticResponse, PlanPeriodDto, getEEParticipationStatusStatistic } from 'core'

import Pie from 'common/components/charts/Pie'

import { Button, Card, Icon, Skeleton, Tooltip } from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import styles from '../index.module.less'

type OnboardingStatusesProps = {
  planPeriod: PlanPeriodDto
}

const OnboardingStatuses = ({ planPeriod }: OnboardingStatusesProps) => {
  const history = useHistory()

  const {
    data: participationStatusStatistic,
    isLoading: isparticipationStatusStatisticLoading,
    sendQuery: sendGetEEParticipationStatus,
  } = useQueryRequestV2<EEParticipationStatusStatisticResponse>({
    initialState: null,
    queryFn: () => getEEParticipationStatusStatistic(planPeriod.id),
  })

  const percentageOptedIn = (
    (participationStatusStatistic?.optedInTotal * 100) /
    participationStatusStatistic?.activeTotal
  ).toFixed(0)

  useEffect(() => {
    sendGetEEParticipationStatus(() => getEEParticipationStatusStatistic(planPeriod.id))
  }, [planPeriod.id])

  return (
    <Skeleton loading={isparticipationStatusStatisticLoading}>
      {participationStatusStatistic && (
        <Card className="h-100 w-100 t-onboarding-statuses">
          <div className="">
            <div className="mb-16 flex jc-space-between align-center">
              <h5 className="mb-0">Eligible employees onboarding statuses</h5>
            </div>
            <div className={styles.statCard}>
              <Button
                className="flex align-center h-100 p-0"
                type="text"
                onClick={() =>
                  history.push({
                    pathname: ROUTE_PATHS.MANAGE_PEOPLE,
                    state: { orderColumn: 'benefitParticipationStatus' },
                  })
                }
              >
                {participationStatusStatistic && (
                  <Pie
                    data={[
                      {
                        type: 'Onboarding Statuses',
                        value: Number(percentageOptedIn),
                        color: '#11D175',
                      },
                    ]}
                    statistics={{
                      title: `${percentageOptedIn}%`,
                      content: `(${participationStatusStatistic?.optedInTotal}/${participationStatusStatistic?.activeTotal})`,
                    }}
                    width={115}
                    height={115}
                    style={{
                      statisticTitle: {
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#27292C',
                      },
                      statisticContent: {
                        fontSize: '14px',
                        marginTop: '4px',
                        color: '#858C96',
                      },
                    }}
                  />
                )}
                <div className="flex ml-8">
                  <div className="text-semi-bold color-gray-9 font-24 mb-8">
                    {participationStatusStatistic.activeTotal} active{' '}
                  </div>
                  <Tooltip title="This is the count of your actively enrolled, benefit-eligible employees (active). The graph shows the current number of opted-in employees over the number of active employees.">
                    <Icon className="far fa-info-circle color-cyan-6 ml-8 mt-8" />
                  </Tooltip>
                </div>
              </Button>
              <Card className={styles.smallCard}>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4"> Pending invite </div>
                    <Tooltip title="These employees have an invitation date set and their formal ICHRA Notice will be sent via email on that date.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">{participationStatusStatistic.newTotal}</div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Invite sent</div>
                    <Tooltip title="These employees have been sent a formal ICHRA Notice and PeopleKeep invitation email, but have not yet logged in to their account.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">{participationStatusStatistic.invitedTotal}</div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Pending decision</div>
                    <Tooltip title="These employees have been invited to the ICHRA and logged in to PeopleKeep, but who have not made an opt-in/out decision.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">{participationStatusStatistic.pendingTotal}</div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Opted in</div>
                    <Tooltip title="These employees have chosen to opt-in to the ICHRA for their current benefit period.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">{participationStatusStatistic.optedInTotal}</div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Opted out</div>
                    <Tooltip title="These employees have chosen to opt-out of the ICHRA for their current benefit period.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">
                    {participationStatusStatistic.optedOutTotal}
                  </div>
                </div>
              </Card>
            </div>

            {/* <div className="flex">
              <div className="mr-16">
                <Badge status="success" className="mr-4" /> Opted in
              </div>
              <div>
                <Badge status="default" className="mr-4" /> Total employees
              </div>
            </div> */}
          </div>
        </Card>
      )}
    </Skeleton>
  )
}

export default OnboardingStatuses
